import { defineStore } from 'pinia'
import { useAuthStore } from "@/stores/auth.store.js";
import { toast } from 'vue-sonner'
import router from '@/router'
import { workspacesApi as api } from '@/api/endpoints/workspaces'
import { useUsersStore } from './users.store';
import { invalidateList } from '@/queries/storeCache'
import { redirectAfterWorkspaceAction } from '@/composables/useWorkspaceRedirect'
import { downloadExcelExport } from '@/lib/export.js'

/** Treat empty / route "null" | "undefined" string artifacts as missing. */
function normalizeWorkspaceId(id) {
  if (id == null || id === '' || id === 'null' || id === 'undefined') return null
  return id
}

/**
 * Latest GetWorkspace() call. Switching workspaces fires overlapping requests;
 * only the newest may write `selectedWorkspace`, so a slow reply for the
 * previous workspace cannot replace the one the user just opened.
 */
let workspaceRequestSeq = 0

export const useWorkspaceStore = defineStore('workspace', {
  state: () => ({
    loadingWorkspaceByID: false,
    selectedWorkspace: null,
    workspaces: [],
    viewType: 'grid',
    hasMultipleWorkspaces: false
  }),
  getters: {
    /** True once the selected workspace has a Chief Compliance Officer. */
    hasCco: (state) => Boolean(state.selectedWorkspace?.cco?.id ?? state.selectedWorkspace?.cco),
    getWorkspace: (state) => {
      return (id) => state.workspaces.find(ws => ws.id == id)
    },
    getWorkspaceID: (state) => {
      const routeId = normalizeWorkspaceId(
        router.currentRoute.value.params.workspace_id,
      )
      const userWorkspaceId = normalizeWorkspaceId(useAuthStore().user?.workspace_id)

      if (useAuthStore().isSuperAdmin) {
        return routeId
      }
      // Multiple workspaces: route wins, otherwise fall back to the user's own.
      return state.hasMultipleWorkspaces
        ? routeId ?? userWorkspaceId
        : userWorkspaceId
    }
  },
  actions: {
    SetupSharedWorkspace(workspaces) {
      this.hasMultipleWorkspaces = workspaces?.length > 1
      this.workspaces = workspaces
    },

    // Kept async: auth.store awaits this during profile hydration.
    async SelectWorkspace(workspace) {
      this.selectedWorkspace = workspace
    },

    // Kept async for the same reason. Guarded so a bad id can't clobber
    // a valid selectedWorkspace with undefined.
    async SelectWorkspaceByID(id) {
      // GetWorkspace() already stored the full record (sections, cco, …) for
      // this id. The list entry is a lighter summary — don't downgrade to it.
      if (this.selectedWorkspace?.id == id) return
      const workspace = this.workspaces.find(ws => ws.id == id)
      if (workspace) this.selectedWorkspace = workspace
    },

    GetSelectWorkspaceSections() {
      if (!this.selectedWorkspace) return [];
      return this.selectedWorkspace.sections.map(section => ({
        label: section.title,
        value: section.id
      }))
    },

    async GetWorkspace(id = undefined) {
      const workspaceId = id ?? this.getWorkspaceID
      if (!workspaceId) {
        router.go(-1)
        return
      }

      const requestSeq = ++workspaceRequestSeq
      this.loadingWorkspaceByID = true
      try {
        const body = await api.get(workspaceId)
        const { workspace } = body.data
        // A newer GetWorkspace() started meanwhile — its result wins.
        if (requestSeq !== workspaceRequestSeq) return workspace
        this.selectedWorkspace = workspace
        return workspace
      } finally {
        if (requestSeq === workspaceRequestSeq) this.loadingWorkspaceByID = false
      }
    },

    async GetAllQuestionnaires(workspace_id, params) {
      const body = await api.questionnaires(workspace_id, params)
      return body.data
    },

    /**
     * `users.store.js` wraps this identical `/workspaces/{id}/users` endpoint
     * and caches unpaginated ("all users") results — reuse that cache instead
     * of hitting the network a second time whenever this is the workspace the
     * caller is already in (the overwhelming majority of call sites). Falls
     * back to a direct, uncached fetch for the rare cross-workspace case
     * (e.g. a super-admin view of a different workspace).
     */
    async GetAllUsers(workspace_id, params, refresh = false) {
      if (normalizeWorkspaceId(workspace_id) === this.getWorkspaceID) {
        return useUsersStore().GetAllUsers(params, refresh)
      }
      const body = await api.users(workspace_id, params)
      return body.data
    },

    async ExportUsers(workspace_id, params) {
      return downloadExcelExport(`/workspaces/${workspace_id}/users/export`, {
        params,
        filename: 'users.xlsx',
      })
    },

    async GetUser(workspace_id, user_id) {
      const body = await api.user(workspace_id, user_id)
      return body.data.user
    },

    async GetUserHistory(workspace_id, user_id, params) {
      const body = await api.userHistory(workspace_id, user_id, params)
      return body.data
    },

    async CreateUser(workspace_id, payload) {
      const body = await api.createUser(workspace_id, payload)
      toast.success(body.message)
      // Cache from GetAllUsers is now stale — clear it so the next call refetches.
      useUsersStore().invalidateUsersCache()
      invalidateList('workspaces')
      return body.data
    },

    async UpdateUser(workspace_id, user_id, payload) {
      const body = await api.updateUser(workspace_id, user_id, payload)
      toast.success(body.message)
      useUsersStore().invalidateUsersCache()
      invalidateList('workspaces')
      return body.data
    },

    async BulkUpdateUserStatus(workspace_id, payload) {
      const body = await api.bulkUpdateUserStatus(workspace_id, payload)
      toast.success(body.message ?? 'Statuses updated')
      useUsersStore().invalidateUsersCache()
      invalidateList('workspaces')
      return body.data
    },

    async DeleteUser(workspace_id, user_id) {
      const body = await api.deleteUser(workspace_id, user_id)
      toast.success(body.message)
      useUsersStore().invalidateUsersCache()
      invalidateList('workspaces')
      return body.data
    },

    async GetQuestionaires() {
      const body = await api.globalQuestionnaires()
      return body.data
    },

    async GetQuestionnaireAiFill(workspace_id) {
      const body = await api.questionnaireAiFill(workspace_id)
      toast.success(body.message)
      return body.data
    },

    async CreateWorkspace(payload) {
      const body = await api.create(payload)
      toast.success(body.message)
      const data = body.data
      const workspace = data?.workspace ?? data
      if (workspace?.id) {
        this.selectedWorkspace = workspace
        const authStore = useAuthStore()
        if (
          !authStore.isSuperAdmin &&
          !authStore.defaultWorkspaceId &&
          !authStore.user?.workspace_id
        ) {
          authStore.ApplyDefaultWorkspace(workspace)
        }
      }
      redirectAfterWorkspaceAction()
      invalidateList('workspaces')
      return data
    },

    async JoinWorkspace(payload) {
      const body = await api.join(payload)
      toast.success(body.message)
      invalidateList('workspaces')
      return body.data
    },

    async UpdateWorkspace(payload) {
      const body = await api.update(this.getWorkspaceID, payload)
      toast.success(body.message)
      // Refresh the selected workspace before redirecting so downstream navigation sees fresh state
      // (GetWorkspace, not GetWorkspaceByID, because it assigns selectedWorkspace); the workspace
      // list queries refetch from the invalidation.
      invalidateList('workspaces')
      await this.GetWorkspace(this.getWorkspaceID)
      redirectAfterWorkspaceAction()
      return body.data
    },

    async UpdateWorkspaceDescription(payload, workspaceId = undefined) {
      const id = workspaceId ?? this.getWorkspaceID
      const body = await api.updateDescription(id, payload)
      toast.success(body.message)
      const { workspace } = body.data ?? {}
      if (workspace) {
        this.selectedWorkspace = workspace
      }
      invalidateList('workspaces')
      return body.data
    },

    // Raw fetch: returns the payload without mutating store state.
    // Prefer GetWorkspace when you need selectedWorkspace updated.
    async GetWorkspaceByID(id = undefined) {
      const body = await api.get(id ?? this.getWorkspaceID)
      return body.data
    },

    async DeleteWorkspace(workspace_id) {
      const body = await api.remove(workspace_id)
      toast.success(body.message)
      invalidateList('workspaces')
      return body.data
    },

    /**
     * Super-admin only: set workspace lifecycle status.
     * API accepts active | inactive | pending; UI currently offers active/inactive.
     */
    async UpdateWorkspaceStatus(workspaceId, status) {
      const body = await api.updateStatus(workspaceId, { status })
      toast.success(body.message ?? 'Workspace status updated')
      invalidateList('workspaces')
      const updated = body.data?.workspace
      const nextStatus = updated?.status ?? status
      this.workspaces = this.workspaces.map((ws) =>
        ws.id == workspaceId
          ? { ...ws, ...(updated || {}), status: nextStatus }
          : ws,
      )
      if (this.selectedWorkspace?.id == workspaceId) {
        this.selectedWorkspace = {
          ...this.selectedWorkspace,
          ...(updated || {}),
          status: nextStatus,
        }
      }
      return body.data
    },

    async GetActivityLogs(params) {
      const body = await api.activityLogs(this.getWorkspaceID, params)
      return body.data
    },

    async GetWorkspaceStats(params) {
      const body = await api.stats(params)
      return body.data
    },

    changeViewType(type) {
      this.viewType = type
    }
  }, persist: {
    pick: ['viewType'],
  }
})